import * as React from 'react';
import { graphql } from 'react-apollo';
import { compose, mapProps } from 'recompose';
import { withRouter } from 'react-router-dom';

import { getCurrentUser as getCurrentUserSelector } from 'selectors/user/selector';
import {
  getTemplateById,
  getCurrentUser,
  userOpenTemplate as userOpenTemplateMutation,
  userCloseTemplate as userCloseTemplateMutation
} from 'graphql/operations';
import { getIdFromUrlParams, isAssessmentReadOnly as isAssessmentReadOnlySelector } from 'selectors/assessment/selector';
import { NETWORK_ONLY } from 'graphql/fetchPolicies';

import { useTranslation, withTranslation } from 'react-i18next';
import { Paper } from '@material-ui/core';

import Questions from 'components/threepp/assessment/Questions/Questions';
import TemplateFooter from 'components/threepp/assessment/Footer/TemplateFooter';
import './NewRecord.scss';

import LoadingPage from 'components/common/Loader/Spinner/Spinner';
import { isFetchingTemplate, getTemplate } from './selector';

export const NewRecordBase = props => {
  const { history, template, isLoading, userOpenTemplate, userCloseTemplate, templateError } = props;
  const { t: translate } = useTranslation();

  const [uploadAttachmentProgressCount, updateuploadAttachmentProgressCount] = React.useState(0);

  const templateId = template?.id;
  const recordId = template?.recordId;
  const isDeleted = template?.isDeleted;
  const isUpdated = template?.isUpdated;

  React.useEffect(() => {
    if (templateId && recordId) {
      if (isDeleted && !isUpdated) {
        history.push('/template-not-found');
      } else if (!isDeleted && !isUpdated) {
        userOpenTemplate({ variables: { templateId, recordId } }).catch(error => {
          console.error('userOpenTemplate OPEN mutation error', error);
        });
      }
    }
  }, [history, templateId, recordId, userOpenTemplate, userCloseTemplate, isDeleted, isUpdated]);

  const incrementAttachmentUploadCount = () => {
    updateuploadAttachmentProgressCount(uploadAttachmentProgressCount + 1);
  };

  const decrementAttachmentUploadCount = () => {
    updateuploadAttachmentProgressCount(uploadAttachmentProgressCount - 1);
  };

  if (isLoading) {
    return <LoadingPage size="lg" expanded />;
  }

  if (templateError) {
    return <div className="assessment-error">{translate('assessment.error')}</div>;
  }

  if (!template) {
    return <div className="assessment-error">{translate('assessment.notFound')}</div>;
  }

  return (
    <div className="new-record-container">
      <div className="assessment-page assessment-row">
        <section className="col-sm-8 new-record-section">
          <Paper elevation={2} className="paper-style new-record-form-container">
            <div className="new-record-title">{translate('newRecord.recordTitle', { templateName: template?.name })}</div>
            <div className="questions-section">
              <Questions
                assessment={template}
                incrementAttachmentUploadCount={incrementAttachmentUploadCount}
                decrementAttachmentUploadCount={decrementAttachmentUploadCount}
                isNewRecord
              />
              <TemplateFooter template={template} t={translate} attachmentUploadCount={uploadAttachmentProgressCount} />
            </div>
          </Paper>
        </section>
      </div>
    </div>
  );
};

export const NewRecordHOC = compose(
  withTranslation(),
  withRouter,
  graphql(getCurrentUser, { name: 'getCurrentUser' }),
  graphql(getTemplateById, {
    options: props => ({
      variables: {
        id: getIdFromUrlParams(props)
      },
      fetchPolicy: NETWORK_ONLY
    })
  }),
  graphql(userOpenTemplateMutation, { name: 'userOpenTemplate' }),
  graphql(userCloseTemplateMutation, { name: 'userCloseTemplate' }),
  mapProps(props => {
    const template = getTemplate(props);
    return {
      t: props.t,
      history: props.history,
      template,
      templateError: props.data.error,
      isLoading: isFetchingTemplate(props),
      currentUser: getCurrentUserSelector(props),
      isAssessmentReadOnly: isAssessmentReadOnlySelector({ template }),
      userOpenTemplate: props.userOpenTemplate,
      userCloseTemplate: props.userCloseTemplate
    };
  })
);

export const NewRecord = NewRecordHOC(NewRecordBase);
